import type { Scene } from '@babylonjs/core/scene';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { FlightFrame } from '../types';
import type { ShotPattern } from '../testing/ShotPattern';
import { ShotTracer } from './ShotTracer';

/** Additive review layer: original comparison paths are never hidden/recolored. */
export class ComparisonReview {
  private meshes: Mesh[] = [];
  private dots: Mesh[] = [];
  private tracers: ShotTracer[] = [];
  private balls: Mesh[] = [];
  private patterns: ShotPattern[] = [];
  private started: number | null = null;
  private patternsVisible = true;
  constructor(private scene: Scene) {}

  show(current: ShotPattern, selected: ShotPattern, selectedHex: string): void {
    this.dispose();
    this.patterns = [current, selected];
    this.patterns.forEach((pattern, index) => {
      const color = Color3.FromHexString(index ? selectedHex : '#e5edf7');
      const mat = new StandardMaterial(`review-${index}`, this.scene);
      mat.disableLighting = true;
      mat.emissiveColor = color;
      const tracer = new ShotTracer(this.scene);
      tracer.setColor(color.r, color.g, color.b);
      // Keep geometry modest even with long rollout recordings.
      const frames = pattern.replay.filter((_, i, all) => i % 8 === 0 || i === all.length - 1);
      tracer.buildFromFrames(frames);
      this.tracers.push(tracer);
      const ball = MeshBuilder.CreateSphere(`review-ball-${index}`, { diameter: 1, segments: 8 }, this.scene);
      ball.material = mat;
      ball.isVisible = false;
      ball.isPickable = false;
      this.balls.push(ball);
      this.meshes.push(ball);
      for (const shot of pattern.shots) {
        const { x, z } = shot.result.landingPosition;
        // Current = hollow ring, selection = filled dot. Shape also communicates identity.
        const dot = index ? MeshBuilder.CreateDisc('landing-selected', { radius: .65, tessellation: 12 }, this.scene)
          : MeshBuilder.CreateTorus('landing-current', { diameter: 1.9, thickness: .15, tessellation: 16 }, this.scene);
        if (index) dot.rotation.x = Math.PI / 2;
        dot.position.set(x, .18 + index * .04, z);
        dot.material = mat;
        dot.isPickable = false;
        this.dots.push(dot);
        this.meshes.push(dot);
      }
    });
    this.focus('selected');
    this.setPatternsVisible(this.patternsVisible);
  }

  focus(which: 'current' | 'selected'): void {
    this.tracers.forEach((tracer, index) => tracer.setSelected(index === (which === 'current' ? 0 : 1)));
  }
  getFramingPoints() {
    return this.patterns.flatMap(pattern => [...pattern.replay.filter((_, i) => i % 16 === 0).map(f => f.position),
      ...pattern.shots.map(s => s.result.landingPosition)]);
  }
  setPatternsVisible(visible: boolean): void {
    this.patternsVisible = visible;
    this.dots.forEach(dot => dot.isVisible = visible);
  }
  replay(): void {
    this.started = performance.now();
    this.balls.forEach(ball => ball.isVisible = true);
  }
  update(): void {
    if (this.started === null) return;
    const time = (performance.now() - this.started) / 1000 * 2;
    this.patterns.forEach((pattern, index) => {
      const p = replayPosition(pattern.replay, time);
      this.balls[index].position.set(p.x, p.y + .5, p.z);
    });
    if (this.patterns.every(p => time >= p.replay[p.replay.length - 1].time + 1)) {
      this.started = null;
      this.balls.forEach(ball => ball.isVisible = false);
    }
  }
  dispose(): void {
    this.started = null;
    const materials = new Set(this.meshes.map(mesh => mesh.material));
    this.meshes.forEach(mesh => mesh.dispose());
    materials.forEach(material => material?.dispose());
    this.tracers.forEach(tracer => tracer.clearAll());
    this.meshes = []; this.dots = []; this.balls = []; this.tracers = []; this.patterns = [];
  }
}

/** Shared elapsed time, not normalized progress: faster flights land first. */
export function replayPosition(frames: FlightFrame[], time: number): Vector3 {
  if (!frames.length) return Vector3.Zero();
  let high = frames.length - 1, low = 0;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (frames[mid].time < time) low = mid + 1; else high = mid;
  }
  const b = frames[low], a = frames[Math.max(0, low - 1)];
  const t = b.time > a.time ? Math.max(0, Math.min(1, (time - a.time) / (b.time - a.time))) : 1;
  return new Vector3(a.position.x + (b.position.x - a.position.x) * t,
    a.position.y + (b.position.y - a.position.y) * t, a.position.z + (b.position.z - a.position.z) * t);
}
